import React, { useState } from 'react';

import { getAudioSystemInfo, clearAudioCache } from '../utils/mobileAudio';
import { useLessonAudioPreloader } from '../hooks/useAudioPreloader';

const MobileAudioDemo: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackResult, setPlaybackResult] = useState<string>('');
  const [systemInfo, setSystemInfo] = useState<any>(null);

  // Demo lesson content for preloading
  const demoContent = [
    { persian: 'سلام', audioUrl: '/audio/vocabulary/greetings/hello.mp3' },
    { persian: 'خداحافظ', audioUrl: '/audio/vocabulary/greetings/goodbye.mp3' },
    { persian: 'ممنون', audioUrl: '/audio/vocabulary/greetings/thank-you.mp3' },
  ];

  const { isPreloading, preloadProgress, preloadedCount, totalCount } = useLessonAudioPreloader(
    demoContent,
    null,
    true
  );

  const handlePlaySample = async (persian: string, audioUrl?: string) => {
    setIsPlaying(true);
    setPlaybackResult('');
    
    try {
      const audio = new Audio(audioUrl);
      await audio.play();
      setPlaybackResult(`✅ Successfully played: ${persian}`);
    } catch (error) {
      setPlaybackResult(`❌ Failed to play: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPlaying(false);
    }
  };

  const handleGetSystemInfo = () => {
    const info = getAudioSystemInfo();
    setSystemInfo(info);
  };

  const handleClearCache = () => {
    clearAudioCache();
    setPlaybackResult('🗑️ Audio cache cleared');
  };

  const handlePreloadTest = async () => {
    const urls = [
      '/audio/vocabulary/greetings/hello.mp3',
      '/audio/vocabulary/greetings/goodbye.mp3',
      '/audio/vocabulary/greetings/thank-you.mp3'
    ];

    try {
      // Simple preload simulation
      setPlaybackResult(`📦 Preload completed: ${urls.length}/${urls.length} successful (simulated)`);
    } catch (error) {
      setPlaybackResult(`❌ Preload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        📱 Mobile Audio Compatibility Demo
        <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
          NEW
        </span>
      </h2>

      {/* System Information */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">System Information</h3>
        <button
          onClick={handleGetSystemInfo}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded mb-3 transition-colors"
        >
          Get System Info
        </button>
        
        {systemInfo && (
          <div className="bg-white p-3 rounded border text-sm font-mono">
            <div>Mobile: {systemInfo.isMobile ? '✅' : '❌'}</div>
            <div>iOS: {systemInfo.isIOS ? '✅' : '❌'}</div>
            <div>Android: {systemInfo.isAndroid ? '✅' : '❌'}</div>
            <div>User Interacted: {systemInfo.userInteracted ? '✅' : '❌'}</div>
            <div>Audio Context: {systemInfo.audioContextState || 'N/A'}</div>
            <div>TTS Available: {systemInfo.speechSynthesisAvailable ? '✅' : '❌'}</div>
            <div>Cache Size: {systemInfo.cacheSize?.audioElements || 0} elements</div>
          </div>
        )}
      </div>

      {/* Audio Preloading Demo */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Audio Preloading</h3>
        
        {isPreloading && (
          <div className="mb-3">
            <div className="text-sm text-gray-600 mb-1">
              Preloading audio files... {preloadedCount}/{totalCount}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${preloadProgress}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={handlePreloadTest}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          disabled={isPreloading}
        >
          {isPreloading ? 'Preloading...' : 'Test Manual Preload'}
        </button>
      </div>

      {/* Audio Playback Demo */}
      <div className="mb-6 p-4 bg-green-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Audio Playback</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {demoContent.map((item, index) => (
            <button
              key={index}
              onClick={() => handlePlaySample(item.persian, item.audioUrl)}
              disabled={isPlaying}
              className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-3 rounded transition-colors flex flex-col items-center"
            >
              <span className="text-lg mb-1">{item.persian}</span>
              <span className="text-xs opacity-80">
                {isPlaying ? '🔊 Playing...' : '🔊 Play Audio'}
              </span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handlePlaySample('این یک تست TTS است')}
            disabled={isPlaying}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            🎤 Test TTS Fallback
          </button>
          
          <button
            onClick={() => handlePlaySample('تست', '/nonexistent-audio.mp3')}
            disabled={isPlaying}
            className="bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white px-4 py-2 rounded transition-colors"
          >
            ⚠️ Test Fallback (Bad URL)
          </button>
        </div>
      </div>

      {/* Cache Management */}
      <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Cache Management</h3>
        <button
          onClick={handleClearCache}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded transition-colors"
        >
          🗑️ Clear Audio Cache
        </button>
      </div>

      {/* Results Display */}
      {playbackResult && (
        <div className="p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Result:</h3>
          <p className="font-mono text-sm">{playbackResult}</p>
        </div>
      )}

      {/* Usage Instructions */}
      <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
        <h3 className="text-lg font-semibold mb-3 text-indigo-800">How It Works</h3>
        <ul className="text-sm text-indigo-700 space-y-2">
          <li>• The system automatically detects your device type (mobile/desktop)</li>
          <li>• Audio files are preloaded in the background for better performance</li>
          <li>• If an audio file fails, it automatically falls back to text-to-speech</li>
          <li>• iOS and Android specific optimizations ensure reliable playback</li>
          <li>• The system caches audio for faster subsequent playback</li>
        </ul>
      </div>

      {/* Mobile Specific Tips */}
      <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <h3 className="text-lg font-semibold mb-3 text-amber-800">Mobile Tips</h3>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>📱 <strong>iPhone users:</strong> Audio requires user interaction first</li>
          <li>🤖 <strong>Android users:</strong> Best performance with Chrome browser</li>
          <li>🔊 <strong>No sound?</strong> Check device volume and silent mode</li>
          <li>📶 <strong>Slow network?</strong> TTS fallback works offline</li>
        </ul>
      </div>
    </div>
  );
};

export default MobileAudioDemo; 